import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getToken, clearToken } from "../../utils/auth";

const API = process.env.REACT_APP_API_URL || window.location.origin;

export default function BranchesSection() {
    const nav = useNavigate();
    const token = getToken();
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const [branches, setBranches] = useState([]);
    const [newName, setNewName] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");

    const [uploadBranchId, setUploadBranchId] = useState(null);
    const [file, setFile] = useState(null);

    const load = async () => {
        try {
            const r = await fetch(`${API}/api/branches`);
            const data = await r.json().catch(() => []);
            setBranches(Array.isArray(data) ? data : []);
        } catch {
            setBranches([]);
        }
    };

    useEffect(() => {
        if (!token) nav("/admin");
        else load();
        // eslint-disable-next-line
    }, []);

    const addBranch = async (e) => {
        e.preventDefault();
        setErr("");

        if (!newName.trim()) {
            return setErr("Şube adı zorunlu");
        }

        const r = await fetch(`${API}/api/branches`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders },
            body: JSON.stringify({ name: newName.trim() }),
        });

        const data = await r.json().catch(() => ({}));
        if (!r.ok) return setErr(data?.error || "Şube eklenemedi");

        setNewName("");
        await load();
    };

    const startEdit = (b) => {
        setEditId(b.id);
        setEditName(b.name);
        setErr("");
    };

    const saveEdit = async () => {
        setErr("");

        if (!editName.trim()) {
            return setErr("Şube adı zorunlu");
        }

        const r = await fetch(`${API}/api/branches/${editId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", ...authHeaders },
            body: JSON.stringify({ name: editName.trim() }),
        });

        const data = await r.json().catch(() => ({}));
        if (!r.ok) return setErr(data?.error || "Güncellenemedi");

        setEditId(null);
        setEditName("");
        await load();
    };

    const delBranch = async (id) => {
        if (!window.confirm("Şubeyi silmek istediğine emin misin? (Fotoğraflar da silinir)")) return;

        const r = await fetch(`${API}/api/branches/${id}`, {
            method: "DELETE",
            headers: { ...authHeaders },
        });

        if (r.status === 401) {
            clearToken();
            nav("/admin");
            return;
        }

        await load();
    };

    const uploadImage = async () => {
        const fd = new FormData();
        fd.append("image", file);

        const r = await fetch(`${API}/api/upload`, {
            method: "POST",
            headers: { ...authHeaders },
            body: fd,
        });

        const text = await r.text();
        let data = {};
        try {
            data = JSON.parse(text);
        } catch {}

        if (!r.ok) throw new Error(data?.error || "Upload başarısız");
        return data.imageUrl;
    };

    const addPhoto = async () => {
        setErr("");

        if (!uploadBranchId) return setErr("Şube seç");
        if (!file) return setErr("Foto seç");

        try {
            setBusy(true);

            const imageUrl = await uploadImage();

            const r = await fetch(`${API}/api/branches/${uploadBranchId}/photos`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders },
                body: JSON.stringify({ imageUrl }),
            });

            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data?.error || "Foto eklenemedi");

            setFile(null);
            await load();
        } catch (e) {
            setErr(e.message || "Hata");
        } finally {
            setBusy(false);
        }
    };

    const delPhoto = async (photoId) => {
        const r = await fetch(`${API}/api/branches/photos/${photoId}`, {
            method: "DELETE",
            headers: { ...authHeaders },
        });

        if (!r.ok) return;
        await load();
    };

    return (
        <div className="container" style={{ padding: "28px 0 60px" }}>
            {err ? <div className="adminError" style={{ marginTop: 10 }}>{err}</div> : null}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
                    gap: "20px",
                    alignItems: "start",
                }}
            >
                <div className="adminCard" style={{ position: "sticky", top: 20 }}>
                    <h3 className="adminH3">Şube Ekle</h3>
                    <form className="adminForm" onSubmit={addBranch}>
                        <input
                            className="adminInput"
                            placeholder="Şube adı (örn. Düzce Merkez)"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                        />
                        <button className="adminBtn" type="submit">
                            Şube Ekle
                        </button>
                    </form>
                </div>

                <div className="adminCard" style={{ position: "sticky", top: 20 }}>
                    <h3 className="adminH3">Şubeye Fotoğraf Ekle</h3>
                    <div className="adminForm">
                        <select
                            className="adminInput"
                            value={uploadBranchId || ""}
                            onChange={(e) =>
                                setUploadBranchId(e.target.value ? Number(e.target.value) : null)
                            }
                        >
                            <option value="">Şube seç</option>
                            {branches.map((b) => (
                                <option key={b.id} value={b.id}>
                                    {b.name}
                                </option>
                            ))}
                        </select>

                        <input
                            className="adminInput"
                            type="file"
                            accept="image/*"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />

                        <button
                            className="adminBtn"
                            type="button"
                            disabled={busy}
                            onClick={addPhoto}
                        >
                            {busy ? "Yükleniyor..." : "Foto Ekle"}
                        </button>
                    </div>
                </div>

                <div className="adminCard">
                    <h3 className="adminH3">Şubeler</h3>

                    <div
                        style={{
                            display: "grid",
                            gap: "18px",
                            maxHeight: "920px",
                            overflowY: "auto",
                            paddingRight: "4px",
                        }}
                    >
                        {branches.map((b) => (
                            <div
                                key={b.id}
                                style={{
                                    border: "1px solid #ececec",
                                    borderRadius: "18px",
                                    padding: "16px",
                                    background: "#fff",
                                    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: "12px",
                                        marginBottom: "14px",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    {editId === b.id ? (
                                        <div style={{ width: "100%" }}>
                                            <input
                                                className="adminInput"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                            />
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: "8px",
                                                    marginTop: "10px",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <button
                                                    className="adminBtnOutline"
                                                    type="button"
                                                    onClick={() => {
                                                        setEditId(null);
                                                        setEditName("");
                                                    }}
                                                >
                                                    İptal
                                                </button>
                                                <button
                                                    className="adminBtn"
                                                    type="button"
                                                    onClick={saveEdit}
                                                >
                                                    Kaydet
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div>
                                                <div
                                                    style={{
                                                        fontSize: "22px",
                                                        fontWeight: 700,
                                                        color: "#222",
                                                        marginBottom: "4px",
                                                    }}
                                                >
                                                    {b.name}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: "13px",
                                                        color: "#777",
                                                    }}
                                                >
                                                    {b.photos?.length || 0} fotoğraf
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: "8px",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <button
                                                    className="adminBtnOutline"
                                                    type="button"
                                                    onClick={() => startEdit(b)}
                                                >
                                                    Düzenle
                                                </button>
                                                <button
                                                    className="adminDanger"
                                                    type="button"
                                                    onClick={() => delBranch(b.id)}
                                                >
                                                    Sil
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {b.photos?.length ? (
                                    <div
                                        style={{
                                            maxHeight: "260px",
                                            overflowY: "auto",
                                            paddingRight: "4px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                                                gap: "12px",
                                            }}
                                        >
                                            {b.photos.map((p) => (
                                                <div
                                                    key={p.id}
                                                    style={{
                                                        position: "relative",
                                                        borderRadius: "14px",
                                                        overflow: "hidden",
                                                        background: "#f5f5f5",
                                                        aspectRatio: "1 / 1",
                                                        boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                                                    }}
                                                >
                                                    <img
                                                        src={`${API}${p.imageUrl}`}
                                                        alt={b.name}
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                            display: "block",
                                                        }}
                                                    />

                                                    <button
                                                        onClick={() => delPhoto(p.id)}
                                                        title="Sil"
                                                        type="button"
                                                        style={{
                                                            position: "absolute",
                                                            top: 8,
                                                            right: 8,
                                                            width: 28,
                                                            height: 28,
                                                            border: "none",
                                                            borderRadius: "50%",
                                                            background: "rgba(0,0,0,0.65)",
                                                            color: "#fff",
                                                            cursor: "pointer",
                                                            fontSize: "15px",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            padding: "18px",
                                            borderRadius: "14px",
                                            background: "#f8f8f8",
                                            color: "#777",
                                            textAlign: "center",
                                            fontSize: "14px",
                                        }}
                                    >
                                        Bu şubeye ait fotoğraf yok
                                    </div>
                                )}
                            </div>
                        ))}

                        {branches.length === 0 ? (
                            <div style={{ color: "#555" }}>Henüz şube yok.</div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}